import useCashbackProgram from '../../hooks/cashback/useCashbackProgram';
import useAuth from '../../hooks/global/useAuth';
import { Trans, useTranslation } from 'react-i18next';
import useAppMethods from '../../hooks/utility/useAppMethods';
import classnames from 'classnames';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { StoreInterface } from '../../store/types';
import { COUPON_TYPES } from '../../services/exports/Constants';
import ReduxActions from '../../store/ReduxActions';
import { actionCreators } from '../../store/actions';
import { Checkbox } from '@mui/material';
import useTheme from '../../hooks/ui/useTheme';
import toast from 'react-hot-toast';
import { ButtonSizes, ButtonTypes, CashbackProgram, OrderData } from '../../services/exports/Interfaces';
import { Card, CardContent } from '../ui/card';
import CustomButton from '../buttons/CustomButton';
import RewardIcon from '../../assets/logo/RewardIcon';

export enum CashbackToggleType {
  Default = 'default',
  Points = 'points',
}

interface Props {
  subtotal: number;
  className?: string;
  type?: CashbackToggleType;
  header?: JSX.Element;
}

interface IConfig extends Props {
  balance: number;
  spendableCashback: number;
  cashbackProgram: CashbackProgram;
  cached_order: Partial<OrderData> | null;
  onToggleClick: (checked: boolean) => void;
}

export default function CashbackToggle(props: Props) {
  const { subtotal, className, type = CashbackToggleType.Default, header } = props;

  const { t } = useTranslation(null, { keyPrefix: 'Components:Checkout:CashbackToggle' });

  const { cached_order } = useSelector((state: StoreInterface) => state.order);

  const { isLoggedIn } = useAuth();
  const { hasCashbackProgram, cashbackProgram, balance, calculateSpendableCashback } = useCashbackProgram();

  const spendableCashback = useMemo(() => calculateSpendableCashback(subtotal), [subtotal, calculateSpendableCashback]);

  const onToggleClick = (checked: boolean) => {
    if (!checked) {
      toast.success(t('cashback.cashback_removed'));
      return ReduxActions.dispatch(
        actionCreators.order.updateOrder({
          coupon_type: null,
          coupon_details: null,
        }),
      );
    }

    toast.success(t('cashback.enabled'));
    return ReduxActions.dispatch(
      actionCreators.order.updateOrder({
        coupon_type: COUPON_TYPES.CASHBACK,
        coupon_details: null,
      }),
    );
  };

  const config: IConfig = {
    className,
    type,
    balance,
    subtotal,
    spendableCashback,
    cashbackProgram,
    cached_order,
    onToggleClick,
    header,
  };

  const renderContent = (): JSX.Element | null => {
    switch (type) {
      case CashbackToggleType.Points:
        return <CashbackToggle.Two {...config} />;
      default:
        return <CashbackToggle.Default {...config} />;
    }
  };

  return hasCashbackProgram && isLoggedIn && renderContent();
}

CashbackToggle.Default = ({
  balance,
  spendableCashback,
  cashbackProgram,
  cached_order,
  onToggleClick,
  className,
}: IConfig) => {
  const { t } = useTranslation(null, { keyPrefix: 'Components:Checkout:CashbackToggle' });
  const theme = useTheme();
  const { formatCurrency } = useAppMethods();

  if (balance === 0) {
    return null;
  }

  if (balance > 0 && spendableCashback === 0) {
    return (
      <div className={classnames('w-full', className)}>
        <p className="text-sm">
          {t('feedback.min_spending_limit_not_reached', { value: cashbackProgram.min_spending_limit })}
        </p>
      </div>
    );
  }

  return (
    <div className={classnames('flex flex-col gap-xs w-full', className)}>
      <p className="text-sm text-brand-text-secondary">{t(`${CashbackToggleType.Default}.optional`)}</p>
      <label className="flex items-start gap-xs cursor-pointer">
        <Checkbox
          checked={cached_order?.coupon_type === COUPON_TYPES.CASHBACK}
          onChange={(e) => onToggleClick(e.target.checked)}
          sx={{
            '& .MuiSvgIcon-root': {
              width: 28,
              height: 28,
            },
            '&.Mui-checked': {
              color: theme.color.primary,
            },
          }}
        />
        <div>
          <p className="text-sm">
            {t(`${CashbackToggleType.Default}.balance`, {
              value: spendableCashback,
              moneyValue: formatCurrency(spendableCashback / 100),
            })}
          </p>
          {!!cashbackProgram.max_spending_limit && (
            <p className="text-xs text-brand-text-secondary">
              {t('feedback.max_spending_limit', { value: cashbackProgram.max_spending_limit })}
            </p>
          )}
        </div>
      </label>
    </div>
  );
};

CashbackToggle.Two = ({
  balance,
  spendableCashback,
  cashbackProgram,
  cached_order,
  onToggleClick,
  className,
  header,
}: IConfig) => {
  const { t } = useTranslation(null, { keyPrefix: 'Components:Checkout:CashbackToggle' });
  const { formatCurrency } = useAppMethods();
  const theme = useTheme();

  if (balance === 0) {
    return null;
  }

  if (balance > 0 && spendableCashback === 0) {
    return (
      <div className={classnames('w-full', className)}>
        <p className="text-sm">
          {t('feedback.min_spending_limit_not_reached', { value: cashbackProgram.min_spending_limit })}
        </p>
      </div>
    );
  }

  return (
    <Card className="bg-accent/5 border-accent/20">
      <CardContent className="p-sm space-y-3">
        {header && header}
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-lg">
                <RewardIcon
                  fill
                  className="w-[20px] h-[20px]"
                  color={theme.buttons[ButtonTypes.primary].text}
                  backgroundColor={theme.buttons[ButtonTypes.primary].background}
                />
              </span>
              <h6 className="font-semibold text-sm text-brand-text-secondary">
                <Trans
                  t={t}
                  values={{ value: spendableCashback, moneyValue: formatCurrency(spendableCashback / 100) }}
                  components={{
                    highlight: <span className={classnames('text-brand-text-primary')} />,
                  }}
                >
                  {`${CashbackToggleType.Points}.use_points`}
                </Trans>
              </h6>
            </div>
            {!!cashbackProgram.max_spending_limit && (
              <p className="text-xs text-brand-text-secondary">
                {t('feedback.max_spending_limit', { value: cashbackProgram.max_spending_limit })}
              </p>
            )}
          </div>
          <CustomButton
            buttonType={cached_order?.coupon_type === COUPON_TYPES.CASHBACK ? ButtonTypes.forth : ButtonTypes.primary}
            buttonSize={ButtonSizes.small}
            onClick={() => onToggleClick(cached_order?.coupon_type !== COUPON_TYPES.CASHBACK)}
            title={
              cached_order?.coupon_type === COUPON_TYPES.CASHBACK
                ? t(`${CashbackToggleType.Points}.buttons.enabled`)
                : t(`${CashbackToggleType.Points}.buttons.use`)
            }
            className="!w-fit pb-[7px] pt-[5px] !px-[12px] rounded-lg"
          />
        </div>
      </CardContent>
    </Card>
  );
};
